import { Cookies } from 'react-cookie';
import { loginService, signUpService, logoutService, loginCodeService } from '../services/auth';
import { KEY_SELECTED_CATEGORY } from '../constants.js/keys';
import { SUBCATEGORIES_VIDEO_LS_PREFIX } from '../constants/subcategoriesFeatureVideo';
import { Mixpanel } from '../constants.js/mixpanel';
import { flushPendingSignupProfile } from '../pages/SignUpV2/onboardingV2.analytics';
import { resolveSignupError } from '../pages/SignUpV2/signupErrors';

export const LOGIN_STATE_TOGGLE = 'auth/LOGIN_STATE_TOGGLE';
export const LOGIN = 'auth/LOGIN';
export const SIGN_UP_STATE_TOGGLE = 'auth/SIGN_UP_STATE_TOGGLE';
export const SIGN_UP = 'auth/SIGN_UP';
export const LOGOUT = 'auth/LOGOUT';
export const SAVE_SIGNUP = 'auth/SAVE_SIGNUP'

const cookies = new Cookies();
const currentUser = cookies.get('user') || {};

const initialState = {
  isUserAuthenticated: false,
  isLoggingIn: false,
  isSigningUp: false,
  userEmail: currentUser.userEmail,
  userType: currentUser.userType,
  userId: currentUser.userId,
  storeId: currentUser.storeId,
  signUpData: {
    email: '',
    password: '',
    store: ''
  }
}

export default (state = initialState, action) => {
  switch (action.type) {
    case LOGIN_STATE_TOGGLE:
      return {
        ...state,
        ...action.payload,
        isLoggingIn: !state.isLoggingIn,
      }

    case LOGIN:
      return {
        ...state,
        ...action.payload,
        isUserAuthenticated: true,
        isLoggingIn: false,
      }

    case SIGN_UP_STATE_TOGGLE:
      return {
        ...state,
        isSigningUp: !state.isSigningUp,
        ...action.payload,
      }

    case LOGOUT:
      return {
        ...state,
        ...action.payload,
        isUserAuthenticated: false,
        userEmail: null,
        userType: null,
        userId: null,
        storeId: null,
        passwordInvalidatedAt: null,
      }

    case SAVE_SIGNUP:
      return {
        ...state,
        signUpData: action.payload,
      }

    default:
      return state
  }
}

/**
 * Calls the loginService and executes the given callback
 * @param {string} email user email
 * @param {string} pass user password
 * @param {function} onLogin callback to run after login success
 * @param {function} onError callback to run after login error
 */
export const logIn = (email, pass, onLogin, onError) => {
  return dispatch => {
    dispatch({ type: LOGIN_STATE_TOGGLE });

    loginService(email, pass)
      .then(({ data }) => {
        const user = {
          userEmail: data.user.email,
          userType: data.user.user_type.id,
          userId: data.user.id,
          userPhone: data.user.mobile_number,
          storeId: data.user.default_store_id,
          createdAt: data.user.created_at,
          passwordInvalidatedAt: data.user.password_invalidated_at,
        };
        cookies.set('user', user, { path: '/' });
        sessionStorage.setItem('pwInvalidatedAt', JSON.stringify(data.user.password_invalidated_at))
        onLogin();
        dispatch({
          type: LOGIN,
          payload: user
        });
        dispatch({ type: SIGN_UP_STATE_TOGGLE, isSigningUp: false });
      })
      .catch(err => {
        onError(err);
        dispatch({ type: LOGIN_STATE_TOGGLE });
      });
  }
}


export const logInCode = (code, onLogin, onError) => {
  return dispatch => {
    dispatch({ type: LOGIN_STATE_TOGGLE });

    loginCodeService(code)
      .then(({ data }) => {
        const user = {
          userEmail: data.user.email,
          userType: data.user.user_type.id,
          userId: data.user.id,
          storeId: data.user.default_store_id,
        };
        cookies.set('user', user, { path: '/' });
        onLogin();
        dispatch({
          type: LOGIN,
          payload: user
        });
        dispatch({ type: SIGN_UP_STATE_TOGGLE, isSigningUp: false });
      })
      .catch(err => {
        console.log(err)
        onError(err);
        dispatch({ type: LOGIN_STATE_TOGGLE });
      });
  }
}

/**
 * Logs the use in withou the api call
 */
export const logInDirectly = () => dispatch => {
  dispatch({ type: LOGIN });
}

/**
 * Runs once the login right after a signup succeeded (the `user` cookie is set by then): links the
 * anonymous signup visit to the new store in Mixpanel and saves the signup profile. This is the only
 * place v1 identifies; regular logins don't.
 */
const identifyNewStore = () => {
  const user = cookies.get('user') || {};
  if (!user.storeId) return;
  Mixpanel.identify(user.storeId);
  flushPendingSignupProfile();
};

/** Translation key for a failed `POST /users` (same mapping as signup v2). */
const signUpErrorMessage = (error) => {
  const outcome = resolveSignupError({ error });
  if (outcome.kind === 'emailInUse') return 'SignUp.error202';
  if (outcome.kind === 'network') return 'SignUpV2.errors.network';
  return outcome.messageKey || 'SignUp.verificationServiceError';
};

/**
 * Creates a new user and then logs the new user in
 * @param {Object} signUpData sign up forms data
 */
export const signUp = (
  signUpData,
  onSignUp,
  onError,
  handleRedirectionWelcome,
  handleRegisterOk
) => {
  return (dispatch) => {
    dispatch({ type: SIGN_UP_STATE_TOGGLE });

    // Signup A/B funnel: only the signups that come from /sign-up carry a variant
    // (the discount page isn't part of the test). The conversion itself is the API's `Sign up`.
    const signupVariant = signUpData.signup_variant;
    const trackFailure = (props) => {
      if (signupVariant) Mixpanel.track('signup_submit_failed', { signup_variant: signupVariant, stage: 'sign_up', ...props });
    };

    signUpService(signUpData)
      .then((res) => {
        const errorCode = res.data && res.data.error_code;
        if (res.status !== 200 || errorCode) {
          trackFailure({
            http_status: res.status,
            error_code: errorCode,
            error_kind: errorCode === 202 ? 'emailInUse' : 'generic',
          });
        }
        if (res.data.error_code === 202) {
          onError("SignUp.error202");
          dispatch({ type: SIGN_UP_STATE_TOGGLE });
        }
        if (res.status === 200) {
          onSignUp(
            signUpData.email,
            signUpData.password,
            identifyNewStore,
            () => false
          );
          handleRegisterOk();
          handleRedirectionWelcome();
        }
      })
      .catch((err) => {
        const body = (err && err.response && err.response.data) || {};
        trackFailure({
          http_status: err && err.response ? err.response.status : null,
          error_code: body.error_code,
          error_kind: err && err.response ? 'generic' : 'network',
          error_message: typeof body.message === 'string' ? body.message.slice(0, 200) : '',
        });
        onError(signUpErrorMessage(err));
        dispatch({ type: SIGN_UP_STATE_TOGGLE });
      });
  };
};

/**
 * Deletes the token cookie and logs out the user
 */
export const logOut = () => dispatch => {
  cookies.remove('user', { path: '/' });
  localStorage.removeItem(KEY_SELECTED_CATEGORY);

  const preservedSubcategoriesVideo = {};
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(SUBCATEGORIES_VIDEO_LS_PREFIX)) {
        preservedSubcategoriesVideo[key] = localStorage.getItem(key);
      }
    }
  } catch {
    /* ignore */
  }

  localStorage.clear();
  sessionStorage.clear();

  try {
    Object.entries(preservedSubcategoriesVideo).forEach(([key, value]) => {
      if (value != null) localStorage.setItem(key, value);
    });
  } catch {
    /* ignore */
  }
  logoutService().then(() => dispatch({
    type: LOGOUT
  }));
}

/**
 * Saves the signUpForm state in the store
 * @param {Object} payload form data
 */
export const saveSignUpData = (payload) => dispatch => {
  dispatch({
    type: SAVE_SIGNUP,
    payload
  });
}

